/**
 * Traffic attribution: which ad or channel a visitor came from.
 *
 * resolveAdAttribution reads the landing URL's tags (feeds the /tutorial visit
 * beacon). captureSignupAttribution remembers them in localStorage on any
 * landing, so the source can still be sent when the visitor signs up later,
 * after navigating away from the tagged URL (sent by register() and Google
 * sign-in, stored per account by the backend, shown only to admins).
 */

// Click-id params ad engines append automatically (no utm_source needed):
// their presence identifies the engine even on a bare destination URL.
const CLICK_ID_SOURCES = [
    ["rdt_cid", "reddit"],
    ["gclid", "google"],
    ["wbraid", "google"],
    ["gbraid", "google"],
    ["fbclid", "facebook"],
    ["msclkid", "bing"],
    ["ttclid", "tiktok"],
    ["twclid", "x"],
    ["li_fat_id", "linkedin"],
];

const STORAGE_KEY = "ftSignupAttribution";
// A tagged visit older than this no longer gets credit for a sign-up
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** Resolve the attribution payload from the landing page's query params. */
export function resolveAdAttribution(searchParams) {
    let source = searchParams.get("source") || searchParams.get("src") || searchParams.get("utm_source");
    if (!source) {
        const match = CLICK_ID_SOURCES.find(([param]) => searchParams.get(param));
        source = match ? match[1] : null;
    }
    return {
        source: source || "direct",
        medium: searchParams.get("utm_medium"),
        campaign: searchParams.get("utm_campaign") || searchParams.get("campaign"),
        content: searchParams.get("utm_content"),
        term: searchParams.get("utm_term"),
        referrer: document.referrer || null,
    };
}

// document.referrer never changes during a single-page session, so only the
// first capture after a page load may treat it as a new arrival. Otherwise the
// navigation right after a sign-up would re-save the ad referrer that was just cleared.
let referrerSeen = false;

function isReloadOrHistoryNavigation() {
    try {
        const type = performance.getEntriesByType("navigation")[0]?.type;
        return type === "reload" || type === "back_forward";
    } catch {
        return false;
    }
}

function externalReferrer() {
    if (referrerSeen) return null;
    referrerSeen = true;
    // A reload or Back keeps the original document.referrer; it isn't a new
    // arrival, and would re-save a referrer cleared by a completed sign-up
    if (isReloadOrHistoryNavigation()) return null;
    try {
        const ref = document.referrer && new URL(document.referrer);
        return ref && ref.host !== window.location.host ? document.referrer : null;
    } catch {
        return null;
    }
}

function readStored() {
    try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (stored && Date.now() - stored.capturedAt < MAX_AGE_MS) return stored;
    } catch { /* unavailable or corrupt */ }
    return null;
}

/**
 * Call on every route change. A tagged landing (source / utm_source / an ad
 * click id) replaces whatever was remembered, so the latest ad gets credit.
 * An untagged arrival from another site is remembered with its referrer (the
 * backend credits X and Reddit referrers to those channels) and replaces an
 * earlier untagged one, but never a tagged visit.
 */
export function captureSignupAttribution(search, pathname) {
    const params = new URLSearchParams(search);
    const resolved = resolveAdAttribution(params);
    const tagged = resolved.source !== "direct";
    const referrer = externalReferrer();
    const stored = readStored();
    if (!tagged && (!referrer || (stored && stored.source !== "direct"))) return;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            ...resolved,
            referrer,
            landingPage: pathname,
            capturedAt: Date.now(),
        }));
    } catch { /* storage unavailable: the sign-up is recorded as direct */ }
}

/** Payload sent with a sign-up; "direct" when nothing (recent) was captured. */
export function getSignupAttribution() {
    const stored = readStored();
    if (!stored) return { source: "direct" };
    const { capturedAt, ...attribution } = stored;
    return attribution;
}

/** After an account is created, so a later sign-up on this browser isn't credited to the same ad. */
export function clearSignupAttribution() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
}
